
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PageTitle } from "@/components/ui/PageTitle";

export const HistoryDifferencesTab = () => {
  const queryClient = useQueryClient();
  const { data: differences, refetch } = useQuery({
    queryKey: ['staff-differences'],
    queryFn: async () => {
      // Récupérer l'utilisateur connecté
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from('counting_discrepancies')
        .select('*')
        .eq('created_by', user.id)
        .order('counting_date', { ascending: false }) // Tri par date de comptage décroissant
        .order('created_at', { ascending: false }); // Tri secondaire par date de création décroissant

      if (error) throw error;
      console.log("Différences reçues:", data);
      return data;
    }
  });

  const handleStatusUpdate = async (id: string, newStatus: "pending" | "salary_deduction" | "forgiven" | "justified" | "validated_as_revenue") => {
    try {
      const { error } = await supabase
        .from('counting_discrepancies')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success("Statut mis à jour avec succès");
      refetch();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Une erreur est survenue");
    }
  };

  const handleAcceptCounted = async (id: string, countedAmount: number, counting_date: string) => {
    try {
      console.log("Tentative d'acceptation du montant:", { id, countedAmount, counting_date });
      
      // 1. Mise à jour du statut de la différence
      const { error: discrepancyError } = await supabase
        .from('counting_discrepancies')
        .update({
          status: "validated_as_revenue" as const,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', id);

      if (discrepancyError) throw discrepancyError;

      // 2. Mise à jour du montant validé
      const { error: validatedError } = await supabase
        .from('validated_amounts')
        .update({
          amount: countedAmount
        })
        .eq('counting_date', counting_date);

      if (validatedError) throw validatedError;
      
      // 3. Invalidation du cache pour forcer le rafraîchissement
      queryClient.invalidateQueries({ queryKey: ['validated-amounts'] });
      queryClient.invalidateQueries({ queryKey: ['staff-differences'] });
      
      toast.success("Montant compté accepté avec succès");
      refetch();
    } catch (error) {
      console.error("Erreur lors de l'acceptation du montant:", error);
      toast.error("Une erreur est survenue");
    }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Historique des montants comptés avec différence" />
      <Card className="p-6">
        <div className="space-y-6">
          {differences?.map((difference) => (
            <Card key={difference.id} className="p-4 border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="font-medium">
                    {new Date(difference.counting_date).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Montant attendu: {difference.expected_amount.toLocaleString()} FCFA
                  </div>
                  <div className="text-sm text-gray-600">
                    Montant compté: {difference.counted_amount.toLocaleString()} FCFA
                  </div>
                  <div className="text-sm text-gray-600">
                    Différence: {difference.difference_amount.toLocaleString()} FCFA
                  </div>
                  {difference.explanation && (
                    <div className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">Explication:</span> {difference.explanation}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {difference.status === 'pending' && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleAcceptCounted(
                          difference.id, 
                          difference.counted_amount,
                          difference.counting_date
                        )}
                      >
                        Accepter montant compté
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleStatusUpdate(difference.id, "salary_deduction")}
                      >
                        Retenir sur salaire
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(difference.id, "forgiven")}
                      >
                        Pardonner
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-sm font-medium text-gray-700">
                Statut actuel: {
                  difference.status === 'pending' ? 'En attente' :
                  difference.status === 'salary_deduction' ? 'Retenu sur salaire' :
                  difference.status === 'forgiven' ? 'Pardonnée' :
                  difference.status === 'justified' ? 'Justifiée' :
                  difference.status === 'validated_as_revenue' ? 'Montant compté accepté' :
                  difference.status
                }
              </div>
            </Card>
          ))}
          {(!differences || differences.length === 0) && (
            <div className="text-center text-gray-500 py-6">
              Aucune différence trouvée
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
